/**
 * Browser error tracking (#3620): GlitchTip at errors.oceanspace.fi.
 *
 * The DSN comes from staff-api at runtime (the image is the same in jv-test
 * and jv-prod). Without one, nothing is loaded: @sentry/react is a dynamic
 * import, so it is not even downloaded. With one: no tracing, no session
 * tracking, minimal data collection, and every event and breadcrumb through
 * the shared personal data scrubber.
 */
import type * as SentryReact from '@sentry/react';
import { scrubBreadcrumb, scrubErrorEvent } from '@vehiclespace/shared/error-tracking';
import { fetchPublicErrorTracking, type BrowserErrorTrackingConfig } from '@/api/publicEnvironment';

type SentrySdk = Pick<typeof SentryReact, 'init' | 'captureException'>;

let sdk: SentrySdk | null = null;

export async function startErrorTracking(
  service: string,
  load: () => Promise<BrowserErrorTrackingConfig | null> = fetchPublicErrorTracking,
  importSdk: () => Promise<SentrySdk> = () => import('@sentry/react'),
): Promise<boolean> {
  let config: BrowserErrorTrackingConfig | null;
  try {
    config = await load();
  } catch {
    return false;
  }
  if (!config) return false;
  const Sentry = await importSdk();
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    ...(config.release ? { release: config.release } : {}),
    tracesSampleRate: 0,
    integrations: (defaults) => defaults.filter((integration) => integration.name !== 'BrowserSession'),
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    beforeSend: (event) => scrubErrorEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    initialScope: { tags: { service } },
  });
  sdk = Sentry;
  return true;
}

/** From ErrorBoundary: React render errors never reach window.onerror. */
export function reportBoundaryError(error: Error, componentStack: string | null | undefined): void {
  sdk?.captureException(error, componentStack ? { contexts: { react: { componentStack } } } : undefined);
}

/** Test seam. */
export function resetErrorTracking(): void {
  sdk = null;
}
